// 商品名稱欄位顯示規則
// 商品 / 套餐 / 套餐子項 name 格式： 商品名稱,前台顯示名稱,列印名稱（3段）
// 規格選項 specOptions[].name 已不拆解逗號，直接取值；列印另有獨立 printName 欄位（優先）
// 各情境取用規則集中於此，name 無逗號時一律回傳第1段＝原本行為不變

// 將 name 拆成三段（trim），缺段為空字串
function splitName(name) {
  const parts = String(name ?? '').split(',')
  return {
    base: (parts[0] ?? '').trim(),
    frontEnd: (parts[1] ?? '').trim(),
    print: (parts[2] ?? '').trim(),
  }
}

// ── 商品 / 套餐 / 套餐子項（3段）──

// 商品名稱（第1段）。注意：送 API 已改用前台顯示名稱(第2段)，此函式目前未使用，保留供取第1段時用。
export function getBaseName(name) {
  return splitName(name).base
}

// 前台顯示：前台顯示名稱(第2段) → fallback 商品名稱(第1段)
export function getFrontEndName(name) {
  const p = splitName(name)
  return p.frontEnd || p.base
}

// 雲端列印：列印名稱(第3段) → fallback 商品名稱(第1段)
export function getPrintName(name) {
  const p = splitName(name)
  return p.print || p.base
}

// 規格群組標題：frontEndDisplayName 有值用它，
// 否則用 stickerTagPrintName，都沒有則用 name（不再逗號拆解，直接取值）
export function getSpecFrontEndName(spec) {
  if (spec?.frontEndDisplayName) return spec.frontEndDisplayName
  if (spec?.stickerTagPrintName) return spec.stickerTagPrintName
  return spec?.name || ''
}

// ── 規格選項 specOptions[]（name 已不拆解逗號，直接取值）──

// 規格選項前台：直接取 name
export function getSpecOptionFrontEndName(name) {
  return name || ''
}

// 規格選項列印：option.printName 優先 → fallback option.name
export function getSpecOptionPrintName(option) {
  if (option && typeof option === 'object') return option.printName || option.name || ''
  return option || ''
}

// ── 送 API：將商品物件內所有名稱拆成 name / displayName / printName──
// 送後端的商品/套餐/子項名稱：
// name = 商品名稱(第1段)
// displayName = 顯示名稱(第2段→fallback第1段)
// printName = 列印名稱(第3段→fallback第1段)
// 規格選項 name 已不拆解逗號，送 API 維持原值，printName（若有）一併保留。
// 不可直接修改原物件（購物車仍在使用），需 clone 後處理。
// 涵蓋：商品/套餐 name、套餐子項 fixedProducts / comboSubItem / comboSubAnyProducts 內的商品名稱。
export function normalizeProductNameForApi(product) {
  if (!product || typeof product !== 'object') return product
  const cloned = structuredClone(product)
  normalizeNamesInPlace(cloned)
  return cloned
}

// ── 規格選項 V3 價格 / 上下架解析 ─────────────────
// 規則對齊商品層（spreadsheet.module.js products getter）。
// standardPriceV3 / availabilityV3：逗號分隔，取 LINE（index 1）。

// standardPriceV3 → LINE 售價。
//   length>=2 取 index1；length===1 取 index0；
//   無值（length 0）→ fallback 回選項原 price。
export function getSpecOptionV3Price(standardPriceV3, fallbackPrice) {
  const parts = standardPriceV3 ? String(standardPriceV3).split(',').map(p => p.trim()) : []
  if (parts.length >= 2) return Number(parts[1])
  if (parts.length === 1) return Number(parts[0])
  return fallbackPrice
}

// availabilityV3 → LINE 上下架。
//   無欄位 / null / 空 → 上架；其餘一律看 index1（'1' 才上架）。
//   單值無 index1 → undefined → 下架（不顯示）。
export function getSpecOptionV3Available(availabilityV3) {
  if (availabilityV3 === null || availabilityV3 === undefined || availabilityV3 === '') return true
  const parts = String(availabilityV3).split(',').map(p => p.trim())
  return parts[1] === '1'
}

// 遞迴將物件 / 陣列中的 name（商品段→第1段，另補 displayName / printName）正規化
// specOptions[].name 已不拆解逗號，維持原值連同 printName 一併送出
function normalizeNamesInPlace(node, context = '') {
  if (Array.isArray(node)) {
    node.forEach(item => normalizeNamesInPlace(item, context))
    return
  }
  if (!node || typeof node !== 'object') return

  if (context === 'specOptions') {
    // name 已不拆解逗號，直接送原值；printName（若有）一併保留送出
    return
  }

  const hasSpecOptions = Array.isArray(node.specOptions)

  // 規格群組標題維持既有送 API 行為：前台顯示名稱（第2段→fallback第1段）
  if (hasSpecOptions && typeof node.name === 'string') {
    node.name = getFrontEndName(node.name)
  } else if (typeof node.name === 'string') {
    // 商品 / 套餐 / 子項名稱 → name / displayName / printName
    const p = splitName(node.name)
    node.name = p.base
    node.displayName = p.frontEnd || p.base
    node.printName = p.print || p.base
  }

  // 規格選項 name 已不拆解逗號，printName（若有）維持原樣一併送出，無需處理

  // 遞迴處理可能巢狀商品的欄位
  for (const [key, value] of Object.entries(node)) {
    if (value && typeof value === 'object') normalizeNamesInPlace(value, key)
  }
}
